import { ApolloLink, Observable } from "@apollo/client";
import { context, propagation, trace } from "@opentelemetry/api";

/**
 * PGS-3499: injects a W3C trace context (traceparent/tracestate) into
 * outbound GraphQL requests, so the resulting server span (PGS-3059) is a
 * child of a real span rather than the root of a fresh, disconnected trace.
 * Mirrors gatorade-storefront's `traceparentLink.ts` (PGS-3060); see that
 * file's history for why this creates its own span instead of injecting
 * whatever `context.active()` happens to be — the same fix applies here,
 * before it ever ships broken: `context.active()` is empty for any GraphQL
 * call firing after the page-load span ends (a `useQuery` on mount, a later
 * user interaction), the common case, so a link that only reads the ambient
 * context silently sends no header at all.
 *
 * Unlike gatorade-storefront's link, this one isn't scoped to a `pep3`
 * project — each of these apps' Apollo client only ever talks to its own
 * pepdirect API (no CMS/upload/aiDesign multiplexing), so there's nothing
 * else to exclude.
 *
 * Built on Apollo Client 3's `Observable` (zen-observable-based) rather than
 * rxjs — v3 doesn't have rxjs's `finalize` operator, so the span is ended
 * exactly once via a guard covering every termination path: complete,
 * error, or unsubscribe (component unmount, changed variables).
 */
export const otelLink = new ApolloLink((operation, forward) => {
  const span = trace
    .getTracer("pepdirect-storefront-apollo")
    .startSpan(`GraphQL ${operation.operationName || "anonymous"}`);

  const headers: Record<string, string> = {};
  propagation.inject(trace.setSpan(context.active(), span), headers);

  operation.setContext(
    ({ headers: existingHeaders }: { headers?: Record<string, string> }) => ({
      headers: { ...existingHeaders, ...headers },
    })
  );

  return new Observable((observer) => {
    let ended = false;
    const endSpanOnce = () => {
      if (!ended) {
        ended = true;
        span.end();
      }
    };

    // Subscribing inside `context.with` keeps this span active for the
    // response and error callbacks that `ZoneContextManager` carries across
    // the fetch. Without it, the app's `errorLink` (above this link) runs
    // `Sentry.captureException` with no active span, so PGS-3511's
    // `tagEventWithActiveOtelTrace` has nothing to tag.
    const subscription = context.with(
      trace.setSpan(context.active(), span),
      () =>
        forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (error) => {
            endSpanOnce();
            observer.error(error);
          },
          complete: () => {
            endSpanOnce();
            observer.complete();
          },
        })
    );

    return () => {
      endSpanOnce();
      subscription.unsubscribe();
    };
  });
});
